/* CRTD Light Play — fragment-shader stylised diamond with cursor-tracked light.
   Lazy-loads on intersection. Vanilla WebGL, no library. */
(function () {
  'use strict';
  if (typeof IntersectionObserver === 'undefined' || typeof WebGLRenderingContext === 'undefined') return;

  const FRAGMENT_SHADER = `
    precision highp float;
    uniform vec2 u_resolution;
    uniform vec2 u_mouse;
    uniform float u_time;
    uniform float u_intensity;

    // Signed distance to a diamond shape (kite cross-section)
    float sdDiamond(vec2 p, float r) {
      p = abs(p);
      float ndp = max(p.x * 0.6 + p.y * 0.8, p.x);
      return ndp - r;
    }

    // Refraction caustics approximation
    vec3 caustic(vec2 uv, vec2 light) {
      vec2 d = uv - light;
      float a = atan(d.y, d.x);
      float r = length(d);
      // Simulate dispersion by sampling at 3 wavelengths
      float band = 0.5 + 0.5 * sin(20.0 * r - 4.0 * a + u_time * 0.5);
      return vec3(
        smoothstep(0.4, 1.0, band - 0.05),
        smoothstep(0.4, 1.0, band),
        smoothstep(0.4, 1.0, band + 0.05)
      ) * (1.0 / (1.0 + r * 4.0));
    }

    void main() {
      vec2 uv = (gl_FragCoord.xy / u_resolution) * 2.0 - 1.0;
      uv.x *= u_resolution.x / u_resolution.y;
      vec2 light = (u_mouse / u_resolution) * 2.0 - 1.0;
      light.x *= u_resolution.x / u_resolution.y;

      float d = sdDiamond(uv, 0.65);
      float inside = smoothstep(0.005, -0.005, d);
      float edge = smoothstep(0.02, 0.0, abs(d));

      // Background warm-grey gradient
      vec3 bg = mix(vec3(0.96), vec3(0.86), uv.y * 0.5 + 0.5);

      // Inside diamond: caustic shimmer + table reflection
      vec3 stone = vec3(0.94);
      vec3 cau = caustic(uv * 1.5, light) * (u_intensity / 100.0);
      stone += cau * 0.8;

      // Internal reflection bands
      float bands = 0.5 + 0.5 * sin(uv.y * 18.0 + u_time * 0.3);
      stone *= 0.85 + 0.15 * bands;

      // Total internal reflection edge brightness
      stone += edge * 0.4;

      vec3 color = mix(bg, stone, inside);

      // Subtle cursor highlight
      vec2 m = (u_mouse / u_resolution) * 2.0 - 1.0;
      m.x *= u_resolution.x / u_resolution.y;
      float spot = exp(-length(uv - m) * 4.0) * 0.15;
      color += spot;

      gl_FragColor = vec4(color, 1.0);
    }
  `;

  const VERTEX_SHADER = `
    attribute vec2 a_pos;
    void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
  `;

  function init(stage) {
    const canvas = stage.querySelector('.crtd-lp-canvas');
    if (!canvas) return;
    const gl = canvas.getContext('webgl', { antialias: true, alpha: false });
    if (!gl) return;

    function compile(type, src) {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        console.warn('CRTD Light Play shader compile error:', gl.getShaderInfoLog(s));
        return null;
      }
      return s;
    }

    const vs = compile(gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('CRTD Light Play program link error:', gl.getProgramInfoLog(prog));
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uRes       = gl.getUniformLocation(prog, 'u_resolution');
    const uMouse     = gl.getUniformLocation(prog, 'u_mouse');
    const uTime      = gl.getUniformLocation(prog, 'u_time');
    const uIntensity = gl.getUniformLocation(prog, 'u_intensity');

    const intensity   = parseFloat(stage.dataset.intensity || '70');
    const mouse       = { x: 0.5, y: 0.5 };
    const targetMouse = { x: 0.5, y: 0.5 };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      canvas.width  = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
    }

    stage.addEventListener('mousemove', function (e) {
      const r = stage.getBoundingClientRect();
      targetMouse.x = (e.clientX - r.left) / r.width;
      targetMouse.y = 1 - (e.clientY - r.top) / r.height;
    });

    stage.addEventListener('touchmove', function (e) {
      const t = e.touches[0];
      const r = stage.getBoundingClientRect();
      targetMouse.x = (t.clientX - r.left) / r.width;
      targetMouse.y = 1 - (t.clientY - r.top) / r.height;
    }, { passive: true });

    resize();
    window.addEventListener('resize', resize);

    const start = performance.now();
    let raf = null;

    function tick() {
      // Smooth mouse follow
      mouse.x += (targetMouse.x - mouse.x) * 0.08;
      mouse.y += (targetMouse.y - mouse.y) * 0.08;

      const t = (performance.now() - start) / 1000;
      // Reduced motion: slow drift instead of cursor tracking
      const mx = reduceMotion ? 0.5 + Math.sin(t * 0.3) * 0.3 : mouse.x;
      const my = reduceMotion ? 0.5 + Math.cos(t * 0.4) * 0.3 : mouse.y;

      gl.uniform2f(uRes,       canvas.width, canvas.height);
      gl.uniform2f(uMouse,     mx * canvas.width, my * canvas.height);
      gl.uniform1f(uTime,      t);
      gl.uniform1f(uIntensity, intensity);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      raf = requestAnimationFrame(tick);
    }

    raf = requestAnimationFrame(tick);

    // Pause rendering when scrolled offscreen
    const visObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          if (!raf) raf = requestAnimationFrame(tick);
        } else {
          if (raf) { cancelAnimationFrame(raf); raf = null; }
        }
      });
    });
    visObs.observe(stage);
  }

  // Lazy-init on first intersection (200px pre-load margin)
  const stages = document.querySelectorAll('[data-crtd-light-play]');
  if (stages.length === 0) return;

  const initObs = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        init(e.target);
        initObs.unobserve(e.target);
      }
    });
  }, { rootMargin: '200px' });

  stages.forEach(function (s) { initObs.observe(s); });
})();
